<template>
  <mailbox-settings-layout :name="formData.name">
    <el-card class="mailbox-settings-outgoing-email">
      <template #header>
        <h3>Outgoing Email</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          This is the email address your customers will see your emails coming from. It is
          usually the same as the address you are auto-forwarding from.
          But, you can set it to any address you own.
        </p>
        <!-- Default email, nothing pending -->
        <template v-if="hasNotAttemptedCustomFrom">
          <div class="dd-divider" />
          <div class="setup-note">
            <i class="ddi ddi-warning" />
            <p>
              You have not set up a custom outgoing email address. This is required to create new conversations.
              <a href="#" class="just-a-link" @click.prevent="fromDialogVisible = true">
                Set one up now...
              </a>
            </p>
          </div>
        </template>

        <p><br></p>
        <email-client-figure
          :from-name="formData.outgoingFromEmail.name"
          :from-mail="formData.outgoingFromEmail.emailAddress"
          @edit-from="fromDialogVisible = true"
        />

        <!-- Pending email verification -->
        <template v-if="hasPendingEmail">
          <div class="dd-divider" />
          <div class="setup-note">
            <i class="ddi ddi-cog" />
            <div>
              <template v-if="pollingForOutgoingEmailConfirmation">
                <template v-if="isPendingEmailAForwardingAddress">
                  <template v-if="hasPostmarkSenderSignatureConfirmationUrl">
                    <h4>Almost there! <a
                      :href="postmarkSenderSignatureConfirmationUrl"
                      target="_blank"
                      class="just-a-link"
                      @click="postmarkUrlClicked=true"
                    >Click here to confirm the address</a>. This screen should update within a few seconds.</h4>
                  </template>
                  <template v-else-if="postmarkUrlClicked">
                    Thanks! Your outgoing email setup should be completed in just a few more seconds!
                  </template>
                  <template v-else>
                    <h4>A confirmation email from Postmark (the service we use to send outgoing emails) is being sent to this mailbox...</h4>
                  </template>
                </template>
                <template v-else>
                  <h4>A confirmation email from <a href="https://postmarkapp.com/" title="Postmark" target="_blank" class="just-a-link">Postmark</a> was just sent to 
                    {{ formData.pendingOutgoingFromEmail.emailAddress }}.</h4>
                  <p>Click the confirmation link in the email and this screen should update within a few seconds.</p>
                </template>
                <p><br></p>
                <verification-loader />
              </template>
              <template v-else>
                <h4>Setting {{ formData.pendingOutgoingFromEmail.emailAddress }} as outgoing email address</h4>
                <p>
                  Sorry, we haven't been able to verify this email. 
                  <a href="#" class="just-a-link" @click.stop.prevent="resendVerificationEmail">Resend the confirmation link</a>
                  or <a href="https://www.donedone.com/help-center" target="_blank" class="just-a-link">contact us for assistance</a>.
                </p>
              </template>
            </div>
          </div>
        </template>

        <!-- Custom email verified -->
        <template v-if="hasCustomFromAddress">
          <div class="dd-divider" />
          <div class="setup-note">
            <i class="ddi ddi-email" />
            <div>
              <h4>{{ formData.outgoingFromEmail.emailAddress }} is verified!</h4>
              <p class="small">
                While not required, you can add <strong>DKIM and Return-Path DNS records</strong> for the 
                <strong>{{ formData.dnsInfo.spfInfo.hostname }}</strong> domain
                to further ensure successful email delivery. Once your records have been added, DoneDone will auto-verify these DNS records, 
                usually within a few minutes.
              </p>
              <dd-button
                v-if="!showDnsRecords"
                size="medium"
                type="flat"
                @click="showDnsRecords = true"
              >Show DNS Records &darr;</dd-button>
              <template v-if="showDnsRecords">
                <div class="dns-info">
                  <template v-if="formData.dnsInfo.dkimVerified">
                    <span class="dns-info__verification success"><i class="ddi ddi-circle-success" /> Verified</span>
                  </template>
                  <template v-else>
                    <span class="dns-info__verification failure"><i class="ddi ddi-circle-error" /> Not Verified</span>
                  </template>
                  <h4>DKIM</h4>
                  <h5>Hostname</h5>
                  <el-tooltip placement="top" content="Click to copy DKIM hostname">
                    <clipboard-text id="dnsDkimHostname" :text="formData.dnsInfo.dkimInfo.hostname" suppress-styles />
                  </el-tooltip>
                  <h5>Type</h5>
                  <div>
                    {{ formData.dnsInfo.dkimInfo.type}}
                  </div>
                  <h5>Value</h5>
                  <el-tooltip placement="top" content="Click to copy DKIM value">
                    <clipboard-text id="dnsDkimValue" :text="formData.dnsInfo.dkimInfo.value" suppress-styles />
                  </el-tooltip>
                  <dd-button
                    v-if="!formData.dnsInfo.dkimVerified"
                    :is-loading="isRefreshingOutgoingEmailSettings"
                    :color="ColorAssistant.MailboxColor"
                    size="medium"
                    @click="getOutgoingEmailSettings"
                  >Refresh for verification</dd-button>
                </div>
                <div class="dns-info">
                  <template v-if="formData.dnsInfo.returnPathVerified">
                    <span class="dns-info__verification success"><i class="ddi ddi-circle-success" /> Verified</span>
                  </template>
                  <template v-else>
                    <span class="dns-info__verification failure"><i class="ddi ddi-circle-error" /> Not Verified</span>
                  </template>
                  <h4>Return-Path</h4>
                  <h5>Hostname</h5>
                  <el-tooltip placement="top" content="Click to copy Return-Path hostname">
                    <clipboard-text id="dnsReturnPathHostname" :text="formData.dnsInfo.returnPathInfo.hostname" suppress-styles />
                  </el-tooltip>
                  <h5>Type</h5>
                  <div>
                    {{ formData.dnsInfo.returnPathInfo.type}}
                  </div>
                  <h5>Value</h5>
                  <el-tooltip placement="top" content="Click to copy Return-Path value">
                    <clipboard-text id="dnsReturnPathValue" :text="formData.dnsInfo.returnPathInfo.value" suppress-styles />
                  </el-tooltip>
                  <dd-button
                    v-if="!formData.dnsInfo.returnPathVerified"
                    :is-loading="isRefreshingOutgoingEmailSettings"
                    :color="ColorAssistant.MailboxColor"
                    size="medium"
                    @click="getOutgoingEmailSettings"
                  >Refresh for verification</dd-button>
                </div>
              </template>
            </div>
          </div>
        </template>

        <!-- MODAL EDIT NAME EMAIL -->
        <dd-modal
          v-model:visible="fromDialogVisible"
          title="Edit Name and Address"
        >
          <el-form ref="fromForm" :model="dialogData" :rules="formRules" label-position="top">
            <el-form-item label="From Name" prop="fromName">
              <el-input ref="fromForm_fromName" v-model="dialogData.fromName" :disabled="isSubmitting" />
            </el-form-item>

            <el-form-item label="From Address" prop="fromAddress">
              <el-input v-model="dialogData.fromAddress" :disabled="isSubmitting" />
            </el-form-item>
          </el-form>
          <template #footer>
            <span>
              <dd-button :is-disabled="isSubmitting" type="text" @click="fromDialogVisible = false">Cancel</dd-button>
              <dd-button
                :is-loading="isSubmitting"
                :color="ColorAssistant.MailboxColor"
                @click="submitFromForm"
              >Verify outgoing email address&hellip;</dd-button>
            </span>
          </template>
        </dd-modal>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import config from '@/config'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import EmailClientFigure from '@/components/global/email-client-figure.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import { IMailboxesOutputOutgoingEmailSettings } from '@/api/types/response.types'
import { IMailboxesInputOutgoingFromAddress } from '@/api/types/request.types'

export default defineComponent({
  name: 'MailboxSettingsOutgoingEmail',

  components: {
    MailboxSettingsLayout,
    EmailClientFigure,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      isRefreshingOutgoingEmailSettings: false,
      fromDialogVisible: false,
      showDnsRecords: false,

      pollingForOutgoingEmailConfirmation: false,
      poller: null as any,
      pollCount: 0,
      pollIntervalInMS: 5000,
      pollMax: 36,

      postmarkSenderSignatureConfirmationUrl: '',
      postmarkUrlClicked: false,

      forwardingEmailAddresses: [] as string[],

      dialogData: {
        fromName: '',
        fromAddress: '',
      },

      formData: {
        name: '',
      } as IMailboxesOutputOutgoingEmailSettings,
      formRules: {
        fromName: [
          { required: true, message: 'Please add a name', trigger: 'submit' },
        ],
        fromAddress: [
          {
            required: true,
            type: 'email',
            message: 'Please enter a valid e-mail address',
            trigger: 'submit',
          },
        ],
      },
    }
  },

  head(): any {
    return {
      title: `Outgoing Email - ${this.formData.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    defaultOutgoingFromAddress(): string {
      return config.defaultOutgoingFromAddress
    },
    hasPendingEmail(): boolean {
      return this.formData.pendingOutgoingFromEmail !== null
    },
    hasCustomFromAddress(): boolean {
      return this.formData.dnsInfo !== null
    },
    hasNotAttemptedCustomFrom(): boolean {
      return !this.hasPendingEmail && !this.hasCustomFromAddress
    },
    isPendingEmailAForwardingAddress(): boolean {
      return (
        this.formData.pendingOutgoingFromEmail !== null &&
        this.forwardingEmailAddresses.includes(
          this.formData.pendingOutgoingFromEmail.emailAddress,
        )
      )
    },
    hasPostmarkSenderSignatureConfirmationUrl(): boolean {
      return this.postmarkSenderSignatureConfirmationUrl !== ''
    },
  },

  watch: {
    fromDialogVisible(newVal) {
      // open modal
      if (newVal) {
        this.$nextTick(() => {
          // @ts-ignore
          this.$refs.fromForm_fromName.focus()
        })
      }
    },
    pollCount: {
      handler(newValue) {
        if (newValue >= this.pollMax) {
          this.endPolling()
        }
      },
    },
  },

  async created() {
    this.isLoading = true
    await Promise.all([
      this.getOutgoingEmailSettings(),
      this.getAutoForwarding(),
    ])
    this.isLoading = false
  },

  beforeUnmount() {
    this.endPolling()
  },

  methods: {
    endPolling() {
      clearInterval(this.poller)
      this.pollingForOutgoingEmailConfirmation = false
    },
    clearPostmarkUrlInfo() {
      this.postmarkSenderSignatureConfirmationUrl = ''
      this.postmarkUrlClicked = false
    },
    async pollForOutgoingEmailConfirmation() {
      // Clear just in case there's any rif-raff...
      this.endPolling()
      this.clearPostmarkUrlInfo()
      this.pollCount = 0
      this.poller = setInterval(async () => {
        this.pollCount++
        await this.confirmEmail()
      }, this.pollIntervalInMS)
      this.pollingForOutgoingEmailConfirmation = true
    },
    async getOutgoingEmailSettings() {
      try {
        this.isRefreshingOutgoingEmailSettings = true
        this.formData = await MailboxesAPI.get_OutgoingEmailSettings(this.id)
        this.dialogData.fromName = this.formData.outgoingFromEmail.name
        this.dialogData.fromAddress = this.formData.outgoingFromEmail.emailAddress
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isRefreshingOutgoingEmailSettings = false
      }
    },
    async getAutoForwarding() {
      try {
        const response = await MailboxesAPI.get_AutoForwarding(this.id)
        this.forwardingEmailAddresses = response.listForwardingEmailAddresses
      } catch (error) {
        this.$error.emit(error)
      }
    },
    submitFromForm() {
      // @ts-ignore
      this.$refs.fromForm.validate(async (valid: boolean) => {
        if (valid) {
          try {
            this.isSubmitting = true
            const data = {
              fromName: this.dialogData.fromName,
              fromEmailAddress: this.dialogData.fromAddress,
            } as IMailboxesInputOutgoingFromAddress
            const response = await MailboxesAPI.post_OutgoingFromAddress(
              this.id,
              data,
            )
            this.$messaging.showSuccess(response.message)
            this.fromDialogVisible = false
          } catch (err) {
            this.$error.emit(err, true)
          } finally {
            this.isSubmitting = false
            await this.getOutgoingEmailSettings()
            if (this.hasPendingEmail) {
              await this.pollForOutgoingEmailConfirmation()
            }
          }
        } else {
          return false
        }
      })
    },
    async resendVerificationEmail() {
      try {
        const response = await MailboxesAPI.get_ResendOutgoingFromAddressVerification(
          this.id,
        )
        this.$messaging.showSuccess(response.message)
        this.pollForOutgoingEmailConfirmation()
      } catch (error) {
        this.$error.emit(error, true)
      }
    },
    async confirmEmail() {
      try {
        // See if there's a Postmark link to click (which would then let the
        // following confirmation call succeed)
        if (!this.hasPostmarkSenderSignatureConfirmationUrl) {
          await this.retrievePostmarkSenderSignatureConfirmationUrl()
        }
        // If still pending, will throw error...
        await MailboxesAPI.post_ConfirmPendingOutgoingFromAddress(this.id)
        // If passes, can assume email was confirmed...
        await this.getOutgoingEmailSettings()
        this.endPolling()
      } catch (error) {
        // Don't emit anything...this is a polling method
      }
    },
    async retrievePostmarkSenderSignatureConfirmationUrl() {
      const response = await MailboxesAPI.get_PostmarkSenderSignatureConfirmationUrl(
        this.id,
      )

      if (response.url && response.url.length > 0) {
        this.postmarkSenderSignatureConfirmationUrl = response.url
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-outgoing-email {
  .setup-note {
    display: flex;
    align-items: flex-start;

    > .ddi {
      font-size: 24px;
      padding-right: 10px;
      color: $color--orange;
    }

    .dd-button {
      margin: 10px 0;
    }

    .dns-info {
      position: relative;
      margin-top: 20px;
      padding: 20px;
      border: $border--solid-light;
      border-radius: 8px;

      &__verification {
        position: absolute;
        right: 20px;
        font-size: 14px;

        &.success {
          color: $mailboxes--theme-color;
        }

        &.failure {
          color: $color--red;
        }
      }

      > div {
        word-break: break-all;
        font-size: 13px;
        border-radius: 8px;
        padding: 10px;
        background-color: var(--color-background-secondary);
        margin-bottom: 18px;
        &.copyable {
          cursor: copy;
        }
      }
    }
  }
}
</style>
